import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  HandCoins,
  ImagePlus,
  MessageSquarePlus,
  Pencil,
  Printer,
  Repeat2,
  Trash2,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  UNIFORM_ART_STATUS_VALUES,
  UNIFORM_LOGISTICS_VALUES,
  UNIFORM_PAYMENT_MARK_VALUES,
  type ProductionOrderResponseType,
  type SaveArtBody,
  type UpdateProductionSheetBody,
} from '@aeris/contracts'
import { uniformGradeFor, type UniformGradeTable } from '@aeris/printing'
import {
  Badge,
  Button,
  CheckboxField,
  ConfirmDialog,
  DateField,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Input,
  Select,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@aeris/ui'
import {
  UNIFORM_SIZES,
  canWork,
  type UniformActor,
  type UniformFunction,
} from '@aeris/uniforms'

import {
  addProductionComment,
  deleteProductionAttachment,
  fetchProductionOrder,
  fetchUniformMembers,
  fetchUniformFabrics,
  fetchUniformPrinters,
  repeatProductionOrder,
  replaceProductionPrinters,
  saveProductionArt,
  saveProductionGrade,
  saveProductionPersonalization,
  saveProductionSheet,
  uploadProductionAttachment,
} from '../lib/api/uniforms.ts'
import { ApiError, fetchSalesDocument } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { dayText, moneyText } from '../lib/production-columns.tsx'
import {
  ART_STATUS_META,
  FUNCTION_LABELS,
  LOGISTICS_LABELS,
  PAYMENT_MARK_META,
  STAGE_META,
  logisticsText,
} from '../lib/production-stage-meta.tsx'
import { timelineText } from '../lib/production-timeline.ts'
import { documentRef } from '../lib/sales-labels.ts'
import {
  draftFromEntries,
  draftFromPersonalization,
  entriesFromDraft,
  gradeTotal,
  incompleteRows,
  personalizationTotal,
  type GradeRowDraft,
  type PersonalizationDraft,
} from '../lib/uniform-grade-draft.ts'
import {
  useCompanyHeader,
  useReceiptTemplate,
} from '../lib/use-receipt-template.ts'
import { useUniformViewer } from '../lib/use-uniform-viewer.ts'

import { OrderDepositDialog } from './order-deposit-dialog.tsx'
import { PrintDocumentDialog } from './print-document-dialog.tsx'
import { ProductionSectors } from './production-sectors.tsx'
import { ProductionStageControl } from './production-stage-control.tsx'
import { SalesDocumentForm } from './sales-document-form.tsx'
import { GradeEditor, PersonalizationEditor } from './uniform-grade-editor.tsx'

/**
 * A página do pedido inteiro — F170, reorganizada por setor na F171.
 *
 * O dono da 4Play pediu "ver tudo num lugar só": o topo diz onde cada setor
 * está (os quatro selos), e as abas são os setores — Atendimento (a ficha, a
 * grade, os personalizados), Arte (o andamento, o designer, as imagens),
 * Produção (a etapa e as máquinas) e a Linha do tempo, com o que cada um fez e
 * o que a equipe comentou.
 *
 * ## Todos veem, cada um mexe na sua parte
 *
 * A aba de outro setor fica só leitura, com a razão escrita. A regra é
 * `canWork` de `@aeris/uniforms` — a mesma com que o servidor recusa.
 *
 * ## Uma versão por setor
 *
 * Ficha, grade e personalizados salvam dentro da versão da ficha; a ARTE tem a
 * dela e a ETAPA também (0098, 0099). A atendente, o designer e o operador
 * mexem no mesmo pedido ao mesmo tempo sem conflito.
 *
 * ## O que esta tela NÃO faz
 *
 * Valor, itens e cliente são o pedido de vendas: "Editar pedido" abre o
 * formulário de sempre. E o sinal daqui é anotação — o dinheiro recebido de
 * verdade é a F171.
 */

type Tab =
  'atendimento' | 'grade' | 'personalizados' | 'arte' | 'producao' | 'linha'

const errorText = (cause: unknown, fallback: string): string =>
  cause instanceof ApiError ? cause.message : fallback

export const UniformOrderSheet = ({
  orderId,
  onClose,
  onOpenOrder,
}: {
  orderId: string
  onClose: () => void
  /** Abre outra ficha — a do pedido repetido, ou a do original. */
  onOpenOrder: (id: string) => void
}): ReactNode => {
  const query = useQuery({
    queryKey: ['production-order', orderId],
    queryFn: async ({ signal }) => await fetchProductionOrder(orderId, signal),
  })

  /*
   * O corpo é montado de novo quando a pessoa pede para recarregar depois de
   * um conflito: o que ela tinha digitado era sobre uma versão que já não é a
   * gravada, e misturar as duas seria pior que recomeçar.
   */
  const [generation, setGeneration] = useState(0)

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title={
          query.data === undefined
            ? 'Ficha de produção'
            : `${documentRef('order', query.data.order.number)} — ${query.data.order.customerName}`
        }
        className="max-w-6xl"
      >
        {query.isPending ? <Spinner label="Carregando a ficha" /> : null}
        {query.isError ? (
          <InlineAlert tone="danger">
            {errorText(query.error, 'Não foi possível carregar a ficha.')}
          </InlineAlert>
        ) : null}
        {query.data === undefined ? null : (
          <SheetBody
            key={`${query.data.order.id}-${String(generation)}`}
            data={query.data}
            onClose={onClose}
            onOpenOrder={onOpenOrder}
            onReload={async () => {
              await query.refetch()
              setGeneration((value) => value + 1)
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

const SheetBody = ({
  data,
  onClose,
  onOpenOrder,
  onReload,
}: {
  data: ProductionOrderResponseType
  onClose: () => void
  onOpenOrder: (id: string) => void
  onReload: () => Promise<void>
}): ReactNode => {
  const queryClient = useQueryClient()
  const { allows } = useAuth()
  const { actor } = useUniformViewer()
  const order = data.order
  const open = order.status === 'draft' || order.status === 'confirmed'
  /** Esta pessoa mexe nesta parte, e o pedido ainda está na fila? (F171) */
  const can = (fn: UniformFunction): boolean =>
    open && allows('uniforms.write') && canWork(actor, fn)
  const canAttend = can('atendimento')
  const canRepeat =
    allows('uniforms.write') &&
    allows('sales.write') &&
    canWork(actor, 'atendimento')

  const [tab, setTab] = useState<Tab>(() => firstTabFor(actor))
  const [version, setVersion] = useState(order.sheetVersion)
  const [conflict, setConflict] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const fabrics = useQuery({
    queryKey: ['uniform-fabrics'],
    queryFn: ({ signal }) => fetchUniformFabrics(signal),
    staleTime: 60_000,
  })

  const [header, setHeader] = useState<UpdateProductionSheetBody>({
    version: order.sheetVersion,
    dispatchDate: order.dispatchDate,
    fabricId: order.fabricId,
    personalized: order.personalized,
    logistics: [...order.logistics],
    eventDate: order.eventDate,
    eventNote: order.eventNote,
    paymentMark: order.paymentMark,
    gradeChecked: order.gradeChecked,
    notes: order.notes,
  })
  const [grade, setGrade] = useState<GradeRowDraft[]>(() =>
    draftFromEntries(data.grade)
  )
  const [people, setPeople] = useState<PersonalizationDraft[]>(() =>
    draftFromPersonalization(data.personalization)
  )

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['production-queue'] })
    await queryClient.invalidateQueries({
      queryKey: ['production-order', order.id],
    })
  }

  const onSaveError = (fallback: string) => (cause: unknown) => {
    if (cause instanceof ApiError && cause.status === 409) {
      setConflict(cause.message)
      return
    }
    setError(errorText(cause, fallback))
  }

  const saved = (message: string) => async (next: number) => {
    setVersion(next)
    setError(null)
    setNotice(message)
    await refresh()
  }

  const saveHeader = useMutation({
    mutationFn: async () =>
      await saveProductionSheet(order.id, { ...header, version }),
    onSuccess: saved('Ficha salva.'),
    onError: onSaveError('Não foi possível salvar a ficha.'),
  })

  const saveGrade = useMutation({
    mutationFn: async () =>
      await saveProductionGrade(order.id, {
        version,
        entries: entriesFromDraft(grade),
      }),
    onSuccess: saved('Grade salva.'),
    onError: onSaveError('Não foi possível salvar a grade.'),
  })

  const savePeople = useMutation({
    mutationFn: async () =>
      await saveProductionPersonalization(order.id, {
        version,
        rows: people.map(({ key: _key, ...row }) => row),
      }),
    onSuccess: saved('Personalizados salvos.'),
    onError: onSaveError('Não foi possível salvar os personalizados.'),
  })

  const [confirmRepeat, setConfirmRepeat] = useState(false)
  const repeat = useMutation({
    mutationFn: async () =>
      await repeatProductionOrder(order.id, { id: crypto.randomUUID() }),
    onSuccess: async (created) => {
      setConfirmRepeat(false)
      await refresh()
      onOpenOrder(created.id)
    },
    onError: (cause: unknown) => {
      setConfirmRepeat(false)
      setError(errorText(cause, 'Não foi possível repetir o pedido.'))
    },
  })

  const [editingOrder, setEditingOrder] = useState(false)
  const salesDocument = useQuery({
    queryKey: ['sales-document', order.id],
    queryFn: async ({ signal }) => await fetchSalesDocument(order.id, signal),
    enabled: editingOrder,
  })

  const [printing, setPrinting] = useState(false)
  /* O sinal de verdade, pelo caixa (F173). */
  const [depositing, setDepositing] = useState(false)
  const canDeposit = allows('sales.deposit') && order.status === 'confirmed'
  const company = useCompanyHeader()
  const template = useReceiptTemplate()

  const pieces = gradeTotal(grade) + personalizationTotal(people)
  const busy =
    saveHeader.isPending || saveGrade.isPending || savePeople.isPending

  const set = <K extends keyof UpdateProductionSheetBody>(
    key: K,
    value: UpdateProductionSheetBody[K]
  ): void => {
    setHeader((current) => ({ ...current, [key]: value }))
  }

  const comments = data.timeline.filter((entry) => entry.type === 'comment')

  return (
    <div className="flex flex-col gap-4">
      {/* O resumo que a linha da planilha dava de uma olhada. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <Summary label="Peças" value={String(pieces)} />
        {/* Nulos para quem só trabalha na arte e/ou na fábrica (F220). */}
        {order.totalAmount === null ? null : (
          <Summary label="Valor" value={moneyText(order.totalAmount)} />
        )}
        {order.paidAmount === null ? null : (
          <Summary label="Pago" value={moneyText(order.paidAmount)} />
        )}
        {order.totalAmount === null ? null : (
          <Summary
            label="Ticket/peça"
            value={
              order.ticketPerPiece === null
                ? '—'
                : moneyText(order.ticketPerPiece)
            }
          />
        )}
        <Summary label="Atendente" value={order.salespersonName ?? '—'} />
        <Summary label="Designer" value={order.designerName ?? '—'} />
        <Summary
          label="Despacho"
          value={
            order.dispatchDate === null ? '—' : dayText(order.dispatchDate)
          }
        />
        <Summary label="Cidade" value={order.customerCity ?? '—'} />
      </div>

      <ProductionSectors order={order} />

      {!open ? (
        <InlineAlert tone="info">
          Este pedido já saiu da fila. A ficha fica para consulta — e para
          repetir.
        </InlineAlert>
      ) : null}

      {conflict === null ? null : (
        <InlineAlert tone="warning" title={conflict}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void onReload()
            }}
          >
            Recarregar a ficha
          </Button>
        </InlineAlert>
      )}
      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}
      {notice === null ? null : (
        <InlineAlert tone="success">{notice}</InlineAlert>
      )}

      <Tabs
        value={tab}
        onValueChange={(value) => {
          setTab(value as Tab)
          setNotice(null)
        }}
      >
        <TabsList>
          <TabsTrigger value="atendimento">Atendimento</TabsTrigger>
          <TabsTrigger value="grade">Grade ({gradeTotal(grade)})</TabsTrigger>
          <TabsTrigger value="personalizados">
            Personalizados ({personalizationTotal(people)})
          </TabsTrigger>
          <TabsTrigger value="arte">
            Arte ({ART_STATUS_META[order.artStatus].label})
          </TabsTrigger>
          <TabsTrigger value="producao">
            Produção ({STAGE_META[order.stage].label})
          </TabsTrigger>
          <TabsTrigger value="linha">
            Linha do tempo
            {comments.length === 0 ? '' : ` (${String(comments.length)})`}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="atendimento">
          <NotYours shown={open && !canAttend} fn="atendimento" />
          <form
            id="ficha-producao"
            className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
            onSubmit={(event) => {
              event.preventDefault()
              saveHeader.mutate()
            }}
          >
            <Field label="Data de despacho">
              {(field) => (
                <DateField
                  {...field}
                  value={header.dispatchDate ?? ''}
                  disabled={!canAttend}
                  onChange={(event) => {
                    set(
                      'dispatchDate',
                      event.target.value === '' ? null : event.target.value
                    )
                  }}
                />
              )}
            </Field>
            <Field label="Tecido">
              {(field) => (
                <Select
                  {...field}
                  value={header.fabricId ?? ''}
                  disabled={!canAttend}
                  onChange={(event) => {
                    set(
                      'fabricId',
                      event.target.value === '' ? null : event.target.value
                    )
                  }}
                >
                  <option value="">A definir</option>
                  {/* O tecido apagado continua aparecendo na ficha que o usa. */}
                  {header.fabricId !== null &&
                    !(fabrics.data ?? []).some(
                      (fabric) => fabric.id === header.fabricId
                    ) && (
                      <option value={header.fabricId}>
                        {order.fabricName ?? 'Tecido apagado'}
                      </option>
                    )}
                  {(fabrics.data ?? []).map((fabric) => (
                    <option key={fabric.id} value={fabric.id}>
                      {fabric.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field
              label="Cortesia"
              hint="O sinal agora é dinheiro de verdade, recebido pelo caixa (Receber sinal). Aqui fica só a marca do pedido de divulgação."
            >
              {(field) => (
                <Select
                  {...field}
                  value={header.paymentMark}
                  disabled={!canAttend}
                  onChange={(event) => {
                    set(
                      'paymentMark',
                      event.target
                        .value as (typeof UNIFORM_PAYMENT_MARK_VALUES)[number]
                    )
                  }}
                >
                  {UNIFORM_PAYMENT_MARK_VALUES.filter(
                    (mark) =>
                      mark === 'nao' ||
                      mark === 'cortesia' ||
                      mark === order.paymentMark
                  ).map((mark) => (
                    <option key={mark} value={mark}>
                      {mark === 'nao'
                        ? 'Não'
                        : mark === 'cortesia'
                          ? 'Cortesia (divulgação)'
                          : `${PAYMENT_MARK_META[mark].label} (anotação antiga)`}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field
              label="Data do evento"
              hint="A corrida, o campeonato — quando existe."
            >
              {(field) => (
                <DateField
                  {...field}
                  value={header.eventDate ?? ''}
                  disabled={!canAttend}
                  onChange={(event) => {
                    set(
                      'eventDate',
                      event.target.value === '' ? null : event.target.value
                    )
                  }}
                />
              )}
            </Field>
            <Field label="Evento">
              {(field) => (
                <Input
                  {...field}
                  value={header.eventNote ?? ''}
                  maxLength={200}
                  placeholder="Corrida dia 26"
                  disabled={!canAttend}
                  onChange={(event) => {
                    set(
                      'eventNote',
                      event.target.value === '' ? null : event.target.value
                    )
                  }}
                />
              )}
            </Field>

            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-(length:--text-sm) font-(--weight-medium)">
                Logística
              </legend>
              {UNIFORM_LOGISTICS_VALUES.map((value) => (
                <CheckboxField
                  key={value}
                  label={LOGISTICS_LABELS[value]}
                  disabled={!canAttend}
                  checked={header.logistics.includes(value)}
                  onCheckedChange={(checked) => {
                    set(
                      'logistics',
                      checked === true
                        ? UNIFORM_LOGISTICS_VALUES.filter(
                            (option) =>
                              option === value ||
                              header.logistics.includes(option)
                          )
                        : header.logistics.filter((option) => option !== value)
                    )
                  }}
                />
              ))}
            </fieldset>

            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-(length:--text-sm) font-(--weight-medium)">
                Com o cliente
              </legend>
              <CheckboxField
                label="Personalizado (nome e número)"
                disabled={!canAttend}
                checked={header.personalized}
                onCheckedChange={(checked) => {
                  set('personalized', checked === true)
                }}
              />
              <CheckboxField
                label="Grade conferida com o cliente"
                disabled={!canAttend}
                checked={header.gradeChecked}
                onCheckedChange={(checked) => {
                  set('gradeChecked', checked === true)
                }}
              />
            </fieldset>

            <div className="sm:col-span-2 lg:col-span-3">
              <Field label="Observações">
                {(field) => (
                  <Textarea
                    {...field}
                    rows={3}
                    maxLength={2000}
                    value={header.notes ?? ''}
                    disabled={!canAttend}
                    onChange={(event) => {
                      set(
                        'notes',
                        event.target.value === '' ? null : event.target.value
                      )
                    }}
                  />
                )}
              </Field>
            </div>
          </form>
          {canAttend ? (
            <div className="mt-3 flex justify-end">
              <Button
                type="submit"
                form="ficha-producao"
                variant="primary"
                disabled={busy}
              >
                Salvar ficha
              </Button>
            </div>
          ) : null}
        </TabsContent>

        <TabsContent value="grade">
          <NotYours shown={open && !canAttend} fn="atendimento" />
          <GradeEditor rows={grade} onChange={setGrade} disabled={!canAttend} />
          {incompleteRows(grade) > 0 ? (
            <InlineAlert tone="warning">
              Há linha com quantidade e sem o nome do modelo ou da peça — ela
              não é salva até ter os dois.
            </InlineAlert>
          ) : null}
          {canAttend ? (
            <div className="mt-3 flex justify-end">
              <Button
                variant="primary"
                disabled={busy}
                onClick={() => {
                  saveGrade.mutate()
                }}
              >
                Salvar grade
              </Button>
            </div>
          ) : null}
        </TabsContent>

        <TabsContent value="personalizados">
          <NotYours shown={open && !canAttend} fn="atendimento" />
          <PersonalizationEditor
            rows={people}
            onChange={setPeople}
            disabled={!canAttend}
          />
          {canAttend ? (
            <div className="mt-3 flex justify-end">
              <Button
                variant="primary"
                disabled={busy || people.some((row) => row.name.trim() === '')}
                onClick={() => {
                  savePeople.mutate()
                }}
              >
                Salvar personalizados
              </Button>
            </div>
          ) : null}
        </TabsContent>

        <TabsContent value="arte">
          <NotYours shown={open && !can('arte')} fn="arte" />
          <ArtSection
            data={data}
            canWrite={can('arte')}
            onSaved={async () => {
              setNotice('Arte salva.')
              setError(null)
              await refresh()
            }}
            onConflict={setConflict}
          />
          <h3 className="mt-6 mb-2 text-(length:--text-sm) font-(--weight-semibold)">
            Imagens
          </h3>
          <Attachments data={data} canWrite={can('arte')} onChanged={refresh} />
        </TabsContent>

        <TabsContent value="producao">
          <NotYours shown={open && !can('producao')} fn="producao" />
          <ProductionSection
            data={data}
            open={open}
            canWrite={can('producao')}
            onOpenOrder={onOpenOrder}
            onSaved={async () => {
              setNotice('Impressoras salvas.')
              setError(null)
              await refresh()
            }}
          />
        </TabsContent>

        <TabsContent value="linha">
          <Timeline data={data} onChanged={refresh} />
        </TabsContent>
      </Tabs>

      <DialogFooter>
        {allows('sales.write') && open && canAttend ? (
          <Button
            variant="ghost"
            leadingIcon={<Pencil aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              setEditingOrder(true)
            }}
          >
            Editar pedido
          </Button>
        ) : null}
        {canDeposit ? (
          <Button
            variant="secondary"
            leadingIcon={<HandCoins aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              setDepositing(true)
            }}
          >
            Receber sinal
          </Button>
        ) : null}
        <Button
          variant="ghost"
          leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
          onClick={() => {
            setPrinting(true)
          }}
        >
          Imprimir grade
        </Button>
        {canRepeat ? (
          <Button
            variant="secondary"
            leadingIcon={<Repeat2 aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              setConfirmRepeat(true)
            }}
          >
            Repetir pedido
          </Button>
        ) : null}
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      </DialogFooter>

      <ConfirmDialog
        open={confirmRepeat}
        onOpenChange={setConfirmRepeat}
        title={`Repetir o ${documentRef('order', order.number)}?`}
        description="Nasce um pedido novo, confirmado, com os mesmos itens e preços, a grade e a arte copiadas. As impressoras deste ficam à vista na ficha nova para sair na mesma máquina, e a arte volta para o designer aprovar."
        confirmLabel="Repetir pedido"
        tone="neutral"
        loading={repeat.isPending}
        onConfirm={() => {
          repeat.mutate()
        }}
      />

      {depositing ? (
        <OrderDepositDialog
          orderId={order.id}
          customerPartyId={order.customerPartyId}
          onClose={() => {
            setDepositing(false)
          }}
          onDone={(message) => {
            setDepositing(false)
            setNotice(message)
            void refresh()
          }}
        />
      ) : null}

      {editingOrder && salesDocument.data !== undefined ? (
        <SalesDocumentForm
          document={salesDocument.data.document}
          onClose={() => {
            setEditingOrder(false)
          }}
          onSaved={() => {
            void refresh()
          }}
        />
      ) : null}

      {printing ? (
        <PrintDocumentDialog
          papers={['a4']}
          initialPaper="a4"
          onClose={() => {
            setPrinting(false)
          }}
          document={uniformGradeFor({
            company,
            template,
            orderLabel: documentRef('order', order.number),
            customerName: order.customerName,
            customerCity: order.customerCity,
            designerName: order.designerName,
            responsibleName: order.salespersonName,
            dispatchDate:
              order.dispatchDate === null ? null : dayText(order.dispatchDate),
            event:
              order.eventDate === null && order.eventNote === null
                ? 'Não'
                : [dayText(order.eventDate), order.eventNote ?? '']
                    .filter((part) => part !== '')
                    .join(' — '),
            fabric: order.fabricName ?? 'A definir',
            logistics: logisticsText(order.logistics),
            pieces,
            tables: gradeTables(grade),
            personalization: people.map((row) => ({
              name: row.name,
              piece: row.piece,
              number: row.number,
              color: row.color,
              quantity: String(row.quantity),
              size: row.size,
            })),
            notes: order.notes,
            printedAt: new Date(),
          })}
        />
      ) : null}
    </div>
  )
}

/** A aba em que a ficha abre: a do setor de quem está olhando. */
const firstTabFor = (actor: UniformActor): Tab => {
  if (actor.manages) return 'atendimento'
  if (actor.functions.includes('atendimento')) return 'atendimento'
  if (actor.functions.includes('arte')) return 'arte'
  if (actor.functions.includes('producao')) return 'producao'
  return 'linha'
}

/** "Esta parte não é sua" — dito, em vez de um formulário todo cinza sem razão. */
const NotYours = ({
  shown,
  fn,
}: {
  shown: boolean
  fn: UniformFunction
}): ReactNode =>
  shown ? (
    <InlineAlert tone="info">
      Só quem é do setor de {FUNCTION_LABELS[fn].sector.toLowerCase()} mexe
      aqui. Você vê, e pode comentar na linha do tempo.
    </InlineAlert>
  ) : null

/**
 * A parte da arte: o andamento, o designer e os arquivos (F171).
 *
 * Salva na versão DA ARTE: marcar "com o cliente" não esbarra na ficha que a
 * atendente está escrevendo.
 */
const ArtSection = ({
  data,
  canWrite,
  onSaved,
  onConflict,
}: {
  data: ProductionOrderResponseType
  canWrite: boolean
  onSaved: () => Promise<void>
  onConflict: (message: string) => void
}): ReactNode => {
  const order = data.order
  const [draft, setDraft] = useState<SaveArtBody>({
    version: order.artVersion,
    status: order.artStatus,
    designerUserId: order.designerUserId,
    artReady: order.artReady,
    productionFileReady: order.productionFileReady,
  })
  const [error, setError] = useState<string | null>(null)

  const members = useQuery({
    queryKey: ['uniform-members'],
    queryFn: async ({ signal }) => await fetchUniformMembers(signal),
  })

  /*
   * Quem desenha primeiro; sem ninguém com a função, a equipe inteira — a
   * loja que ainda não marcou as funções não fica sem poder escolher.
   */
  const designers = (() => {
    const all = members.data ?? []
    const withArt = all.filter((member) => member.functions.includes('arte'))
    return withArt.length === 0 ? all : withArt
  })()

  const save = useMutation({
    mutationFn: async () => await saveProductionArt(order.id, draft),
    onSuccess: async (result) => {
      setDraft((current) => ({ ...current, version: result.artVersion }))
      setError(null)
      await onSaved()
    },
    onError: (cause: unknown) => {
      if (cause instanceof ApiError && cause.status === 409) {
        onConflict(cause.message)
        return
      }
      setError(errorText(cause, 'Não foi possível salvar a arte.'))
    },
  })

  return (
    <form
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <Field label="Andamento" hint={ART_STATUS_META[draft.status].what}>
        {(field) => (
          <Select
            {...field}
            value={draft.status}
            disabled={!canWrite}
            onChange={(event) => {
              setDraft((current) => ({
                ...current,
                status: event.target
                  .value as (typeof UNIFORM_ART_STATUS_VALUES)[number],
              }))
            }}
          >
            {UNIFORM_ART_STATUS_VALUES.map((status) => (
              <option key={status} value={status}>
                {ART_STATUS_META[status].label}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Designer">
        {(field) => (
          <Select
            {...field}
            value={draft.designerUserId ?? ''}
            disabled={!canWrite}
            onChange={(event) => {
              setDraft((current) => ({
                ...current,
                designerUserId:
                  event.target.value === '' ? null : event.target.value,
              }))
            }}
          >
            <option value="">—</option>
            {order.designerUserId !== null &&
            !designers.some(
              (member) => member.userId === order.designerUserId
            ) ? (
              <option value={order.designerUserId}>
                {order.designerName ?? 'Designer'}
              </option>
            ) : null}
            {designers.map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.displayName}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-(length:--text-sm) font-(--weight-medium)">
          Os arquivos
        </legend>
        <CheckboxField
          label="Arte no Drive"
          disabled={!canWrite}
          checked={draft.artReady}
          onCheckedChange={(checked) => {
            setDraft((current) => ({ ...current, artReady: checked === true }))
          }}
        />
        <CheckboxField
          label="Arquivo do Corel"
          disabled={!canWrite}
          checked={draft.productionFileReady}
          onCheckedChange={(checked) => {
            setDraft((current) => ({
              ...current,
              productionFileReady: checked === true,
            }))
          }}
        />
      </fieldset>
      {error === null ? null : (
        <div className="sm:col-span-2 lg:col-span-3">
          <InlineAlert tone="danger">{error}</InlineAlert>
        </div>
      )}
      {canWrite ? (
        <div className="flex justify-end sm:col-span-2 lg:col-span-3">
          <Button type="submit" variant="primary" disabled={save.isPending}>
            Salvar arte
          </Button>
        </div>
      ) : null}
    </form>
  )
}

/**
 * A parte da produção: a etapa, e em quais máquinas o pedido saiu.
 *
 * As impressoras nascem da troca para "Imprimindo"; aqui é a correção — a
 * máquina marcada por engano, a segunda que entrou no meio do caminho.
 */
const ProductionSection = ({
  data,
  open,
  canWrite,
  onOpenOrder,
  onSaved,
}: {
  data: ProductionOrderResponseType
  open: boolean
  canWrite: boolean
  onOpenOrder: (id: string) => void
  onSaved: () => Promise<void>
}): ReactNode => {
  const order = data.order
  const [chosen, setChosen] = useState<readonly string[]>(
    order.printers.map((printer) => printer.id)
  )
  const [error, setError] = useState<string | null>(null)

  const printers = useQuery({
    queryKey: ['uniform-printers', 'active'],
    queryFn: async ({ signal }) => await fetchUniformPrinters('active', signal),
  })

  /** As que se pode marcar: as ativas e as que o pedido já tinha. */
  const options = [
    ...(printers.data ?? []),
    ...order.printers
      .filter(
        (attached) => !(printers.data ?? []).some((p) => p.id === attached.id)
      )
      .map((attached) => ({ ...attached, status: 'archived' as const })),
  ]

  const save = useMutation({
    mutationFn: async () => {
      await replaceProductionPrinters(order.id, { printerIds: [...chosen] })
    },
    onSuccess: async () => {
      setError(null)
      await onSaved()
    },
    onError: (cause: unknown) => {
      setError(errorText(cause, 'Não foi possível salvar as impressoras.'))
    },
  })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-(length:--text-sm) font-(--weight-medium)">
          Etapa
        </span>
        {open ? (
          <ProductionStageControl
            order={order}
            label={`Etapa do ${documentRef('order', order.number)}`}
          />
        ) : (
          <Badge
            tone={STAGE_META[order.stage].tone}
            icon={STAGE_META[order.stage].icon}
          >
            {STAGE_META[order.stage].label}
          </Badge>
        )}
        <span className="text-(length:--text-xs) text-(--text-muted)">
          {STAGE_META[order.stage].what}
        </span>
      </div>

      {data.repeatedFrom === null ? null : (
        <InlineAlert
          tone="info"
          title={`Repetição do ${documentRef('order', data.repeatedFrom.number)}`}
        >
          {data.repeatedFrom.printers.length === 0
            ? 'O pedido original não marcou impressora.'
            : `O original saiu em: ${data.repeatedFrom.printers.map((printer) => printer.name).join(', ')}.`}{' '}
          <button
            type="button"
            className="underline"
            onClick={() => {
              if (data.repeatedFrom !== null) onOpenOrder(data.repeatedFrom.id)
            }}
          >
            Abrir o original
          </button>
        </InlineAlert>
      )}

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-(length:--text-sm) font-(--weight-medium)">
          Impressoras deste pedido
        </legend>
        {options.length === 0 ? (
          <p className="text-(length:--text-sm) text-(--text-muted)">
            Nenhuma cadastrada.
          </p>
        ) : null}
        {options.map((printer) => (
          <CheckboxField
            key={printer.id}
            label={
              printer.status === 'archived'
                ? `${printer.name} (arquivada)`
                : printer.name
            }
            disabled={!canWrite}
            checked={chosen.includes(printer.id)}
            onCheckedChange={(checked) => {
              setChosen((current) =>
                checked === true
                  ? [...current, printer.id]
                  : current.filter((id) => id !== printer.id)
              )
            }}
          />
        ))}
      </fieldset>
      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}
      {canWrite ? (
        <div className="flex justify-end">
          <Button
            variant="secondary"
            disabled={save.isPending}
            onClick={() => {
              save.mutate()
            }}
          >
            Salvar impressoras
          </Button>
        </div>
      ) : null}
    </div>
  )
}

/**
 * A linha do tempo do pedido e a conversa da equipe (F171).
 *
 * Todo mundo que vê a fila comenta — o operador que viu o erro na máquina não
 * tem função na arte, e é justamente ele quem precisa dizer.
 */
const Timeline = ({
  data,
  onChanged,
}: {
  data: ProductionOrderResponseType
  onChanged: () => Promise<void>
}): ReactNode => {
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)

  const send = useMutation({
    mutationFn: async () =>
      await addProductionComment(data.order.id, { body: text }),
    onSuccess: async () => {
      setText('')
      setError(null)
      await onChanged()
    },
    onError: (cause: unknown) => {
      setError(errorText(cause, 'Não foi possível comentar.'))
    },
  })

  return (
    <div className="flex flex-col gap-4">
      <form
        className="flex flex-col gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (text.trim() !== '') send.mutate()
        }}
      >
        <Field label="Comentário da equipe">
          {(field) => (
            <Textarea
              {...field}
              rows={2}
              maxLength={2000}
              value={text}
              placeholder="O cliente pediu o azul mais escuro"
              onChange={(event) => {
                setText(event.target.value)
              }}
            />
          )}
        </Field>
        {error === null ? null : (
          <InlineAlert tone="danger">{error}</InlineAlert>
        )}
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="secondary"
            leadingIcon={
              <MessageSquarePlus aria-hidden="true" className="h-4 w-4" />
            }
            disabled={send.isPending || text.trim() === ''}
          >
            Comentar
          </Button>
        </div>
      </form>

      <ol className="flex flex-col gap-3">
        {data.timeline.map((entry) => {
          const line = timelineText(entry)
          const comment = entry.type === 'comment'
          return (
            <li
              key={`${entry.type}-${entry.id}`}
              className={
                comment
                  ? 'rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-3'
                  : 'border-l-2 border-(--border-subtle) pl-3'
              }
            >
              <p
                className={
                  comment
                    ? 'text-(length:--text-sm) whitespace-pre-wrap text-(--text-primary)'
                    : 'text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)'
                }
              >
                {line.title}
              </p>
              {line.detail === null ? null : (
                <p className="text-(length:--text-xs) text-(--text-secondary)">
                  {line.detail}
                </p>
              )}
              <p className="text-(length:--text-xs) text-(--text-muted)">
                {entry.actorName ?? 'alguém'} ·{' '}
                {new Date(entry.occurredAt).toLocaleString('pt-BR', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
              </p>
            </li>
          )
        })}
        <li className="border-l-2 border-(--border-subtle) pl-3">
          <p className="text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
            Pedido aberto
          </p>
          <p className="text-(length:--text-xs) text-(--text-muted)">
            {new Date(data.order.issuedAt).toLocaleString('pt-BR', {
              dateStyle: 'short',
              timeStyle: 'short',
            })}
          </p>
        </li>
      </ol>
    </div>
  )
}

const Summary = ({
  label,
  value,
}: {
  label: string
  value: string
}): ReactNode => (
  <div>
    <p className="text-(length:--text-xs) text-(--text-muted)">{label}</p>
    <p className="text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
      {value}
    </p>
  </div>
)

/** A grade da tela, uma tabela por modelo, na forma do Excel da loja. */
const gradeTables = (rows: readonly GradeRowDraft[]): UniformGradeTable[] => {
  const blocks = new Map<string, GradeRowDraft[]>()
  for (const row of rows) {
    const total = UNIFORM_SIZES.reduce(
      (sum, size) => sum + (Number(row.quantities[size] ?? '') || 0),
      0
    )
    if (total === 0 || row.block.trim() === '') continue
    blocks.set(row.block.trim(), [...(blocks.get(row.block.trim()) ?? []), row])
  }
  return [...blocks.entries()].map(([title, blockRows]) => {
    const sizes = UNIFORM_SIZES.filter((size) =>
      blockRows.some((row) => (Number(row.quantities[size] ?? '') || 0) > 0)
    )
    const totals = blockRows.map((row) =>
      sizes.reduce(
        (sum, size) => sum + (Number(row.quantities[size] ?? '') || 0),
        0
      )
    )
    return {
      title,
      sizes,
      rows: blockRows.map((row, index) => ({
        piece: row.piece,
        quantities: sizes.map((size) => row.quantities[size] ?? ''),
        total: String(totals[index] ?? 0),
      })),
      total: String(totals.reduce((sum, value) => sum + value, 0)),
    }
  })
}

/**
 * As imagens do pedido — a arte e o print do cliente.
 *
 * Guardadas como referência (WebP de até 1600px), não como arquivo de
 * produção: o Corel continua no Drive, e o checklist da ficha diz se está lá.
 */
const Attachments = ({
  data,
  canWrite,
  onChanged,
}: {
  data: ProductionOrderResponseType
  canWrite: boolean
  onChanged: () => Promise<void>
}): ReactNode => {
  const [error, setError] = useState<string | null>(null)
  const [viewing, setViewing] = useState<string | null>(null)

  const upload = useMutation({
    mutationFn: async (file: File) =>
      await uploadProductionAttachment(data.order.id, file),
    onSuccess: async () => {
      setError(null)
      await onChanged()
    },
    onError: (cause: unknown) => {
      setError(errorText(cause, 'Não foi possível subir a imagem.'))
    },
  })

  const remove = useMutation({
    mutationFn: async (attachmentId: string) => {
      await deleteProductionAttachment(data.order.id, attachmentId)
    },
    onSuccess: async () => {
      setError(null)
      await onChanged()
    },
    onError: (cause: unknown) => {
      setError(errorText(cause, 'Não foi possível tirar a imagem.'))
    },
  })

  return (
    <div className="flex flex-col gap-3">
      {canWrite ? (
        <label className="flex w-fit cursor-pointer items-center gap-2 text-(length:--text-sm) text-(--brand-primary)">
          <ImagePlus aria-hidden="true" className="h-4 w-4" />
          {upload.isPending
            ? 'Enviando…'
            : 'Subir imagem (arte, print do cliente)'}
          <input
            type="file"
            accept="image/*"
            className="sr-only"
            disabled={upload.isPending}
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file !== undefined) upload.mutate(file)
              event.target.value = ''
            }}
          />
        </label>
      ) : null}
      <p className="text-(length:--text-xs) text-(--text-muted)">
        A imagem guardada é para consulta. O arquivo de impressão (Corel)
        continua no Drive.
      </p>
      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}
      {data.attachments.length === 0 ? (
        <p className="text-(length:--text-sm) text-(--text-muted)">
          Nenhuma imagem.
        </p>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(10rem,1fr))] gap-2">
          {data.attachments.map((attachment) => (
            <figure
              key={attachment.id}
              className="flex flex-col gap-1 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface) p-1"
            >
              <button
                type="button"
                className="aspect-square overflow-hidden rounded-(--radius-sm) bg-(--surface-sunken)"
                onClick={() => {
                  setViewing(attachment.url)
                }}
              >
                <img
                  src={attachment.thumbUrl}
                  alt={attachment.caption ?? 'Imagem do pedido'}
                  width={attachment.width}
                  height={attachment.height}
                  loading="lazy"
                  className="h-full w-full object-contain"
                />
              </button>
              {canWrite ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={remove.isPending}
                  leadingIcon={
                    <Trash2 aria-hidden="true" className="h-4 w-4" />
                  }
                  onClick={() => {
                    remove.mutate(attachment.id)
                  }}
                >
                  Tirar
                </Button>
              ) : null}
            </figure>
          ))}
        </div>
      )}
      {viewing === null ? null : (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) setViewing(null)
          }}
        >
          <DialogContent title="Imagem do pedido" className="max-w-4xl">
            <img
              src={viewing}
              alt="Imagem do pedido"
              className="max-h-[75dvh] w-full object-contain"
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
